import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tags, Wallet } from "lucide-react";
import { toast } from "sonner";
import { todayISO } from "@/lib/dateFormat";

const money = (n) => `$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const firstOfMonth = () => `${todayISO().slice(0, 7)}-01`;
const today = () => todayISO();

const emptyForm = () => ({
  date: today(), sede_id: "", category_id: "", description: "",
  amount: "", currency: "USD", vendor: "",
});

export default function Expenses() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);
  const [sedes, setSedes] = useState([]);
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState({ date_from: firstOfMonth(), date_to: today(), sede_id: "all", category_id: "all" });
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [catsOpen, setCatsOpen] = useState(false);
  const [newCat, setNewCat] = useState("");

  const load = async () => {
    const params = { date_from: filters.date_from, date_to: filters.date_to };
    if (filters.sede_id !== "all") params.sede_id = filters.sede_id;
    if (filters.category_id !== "all") params.category_id = filters.category_id;
    const [rExp, rSum, rSedes, rCats] = await Promise.all([
      api.get("/expenses", { params }),
      api.get("/expenses/summary", { params: { date_from: filters.date_from, date_to: filters.date_to } }),
      api.get("/sedes"),
      api.get("/expense-categories"),
    ]);
    setRows(rExp.data); setSum(rSum.data); setSedes(rSedes.data || []); setCats(rCats.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const submit = async () => {
    const body = { ...form, amount: Number(form.amount) };
    try {
      if (editingId) await api.patch(`/expenses/${editingId}`, body);
      else await api.post("/expenses", body);
      toast.success(t("saved"));
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/expenses/${id}`);
    toast.success(t("deleted")); load();
  };

  const activeCats = useMemo(() => cats.filter((c) => c.active !== false), [cats]);

  return (
    <div>
      <PageHeader
        title={t("expenses")}
        subtitle={t("expenses_sub")}
        testId="expenses-header"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCatsOpen(true)} data-testid="expense-categories-button">
              <Tags className="h-4 w-4" /> {t("expense_categories")}
            </Button>
            <Button onClick={() => { setEditingId(null); setForm(emptyForm()); setOpen(true); }} data-testid="new-expense-button">
              <Plus className="h-4 w-4" /> {t("expense_new")}
            </Button>
          </div>
        }
      />

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Card className="p-4" data-testid="expense-total-card">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("expense_total")}</div>
          <div className="font-display text-2xl font-bold text-slate-900">{money(sum?.total_usd)}</div>
          <div className="text-xs text-slate-400">{sum?.count || 0} {t("expense_records")} · TC ₡{sum?.exchange_rate}</div>
        </Card>
        <Card className="p-4 sm:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{t("expense_by_office")}</div>
          <div className="space-y-1" data-testid="expense-by-sede">
            {(sum?.by_sede || []).map((s) => (
              <div key={s.sede_id} className="flex justify-between text-sm">
                <span className="text-slate-700">{s.name}</span>
                <span className="font-semibold text-slate-900">{money(s.total_usd)}</span>
              </div>
            ))}
            {(sum?.by_sede || []).length === 0 && <div className="text-sm text-slate-400">{t("no_data")}</div>}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-xs">{t("from")}</Label>
          <Input type="date" value={filters.date_from} data-testid="exp-filter-from"
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">{t("to")}</Label>
          <Input type="date" value={filters.date_to} data-testid="exp-filter-to"
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">{t("sede")}</Label>
          <Select value={filters.sede_id} onValueChange={(v) => setFilters({ ...filters, sede_id: v })}>
            <SelectTrigger data-testid="exp-filter-sede"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("expense_category")}</Label>
          <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v })}>
            <SelectTrigger data-testid="exp-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {activeCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="expenses-list">
        {rows.length === 0 && (
          <div className="p-10 text-center text-slate-500">
            <Wallet className="h-6 w-6 mx-auto mb-2 text-slate-300" />{t("no_data")}
          </div>
        )}
        {rows.map((r) => (
          <div key={r.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`expense-row-${r.id}`}>
            <div className="w-24 text-xs font-mono text-slate-500">{r.date}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{r.description}</div>
              <div className="text-xs text-slate-500">
                {r.sede_name} · {r.category_name}{r.vendor ? ` · ${r.vendor}` : ""}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-slate-900">{money(r.amount_usd)}</div>
              {r.currency === "CRC" && (
                <div className="text-[11px] text-slate-400">₡{Number(r.amount).toLocaleString("es-CR")}</div>
              )}
            </div>
            <Button variant="ghost" size="icon" data-testid={`edit-expense-${r.id}`}
              onClick={() => {
                setEditingId(r.id);
                setForm({ date: r.date, sede_id: r.sede_id, category_id: r.category_id,
                  description: r.description, amount: String(r.amount), currency: r.currency, vendor: r.vendor || "" });
                setOpen(true);
              }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="text-red-600" data-testid={`delete-expense-${r.id}`}
              onClick={() => remove(r.id)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>

      {/* Create / edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{editingId ? t("edit") : t("expense_new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t("date")}</Label>
                <Input type="date" data-testid="expense-date-input" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>{t("sede")}</Label>
                <Select value={form.sede_id} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                  <SelectTrigger data-testid="expense-sede-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {sedes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("expense_category")}</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {activeCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Input data-testid="expense-description-input" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label>{t("amount")}</Label>
                <Input type="number" step="0.01" data-testid="expense-amount-input" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <Label>{t("currency")}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="CRC">CRC (₡)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("expense_vendor")}</Label>
              <Input data-testid="expense-vendor-input" value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-expense-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories manager */}
      <Dialog open={catsOpen} onOpenChange={setCatsOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("expense_categories")}</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-3">
            <Input placeholder={t("expense_new_category")} value={newCat} data-testid="new-category-input"
              onChange={(e) => setNewCat(e.target.value)} />
            <Button data-testid="add-category-button" onClick={async () => {
              try {
                await api.post("/expense-categories", { name: newCat });
                setNewCat(""); toast.success(t("saved")); load();
              } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
            }}>{t("add")}</Button>
          </div>
          <div className="space-y-2" data-testid="categories-list">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center gap-2" data-testid={`category-row-${c.id}`}>
                <Input defaultValue={c.name} className={c.active === false ? "opacity-50" : ""}
                  onBlur={async (e) => {
                    if (e.target.value.trim() && e.target.value !== c.name) {
                      await api.patch(`/expense-categories/${c.id}`, { name: e.target.value.trim() });
                      toast.success(t("saved")); load();
                    }
                  }} />
                <Button variant="ghost" size="icon" className="text-red-600" data-testid={`delete-category-${c.id}`}
                  onClick={async () => {
                    if (!window.confirm(t("confirm_delete"))) return;
                    const r = await api.delete(`/expense-categories/${c.id}`);
                    toast.success(r.data.archived ? t("expense_category_archived") : t("deleted"));
                    load();
                  }}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
